/*!
 * ymm-groups.js — separates a collection's product grid into fitment groups with their own headings:
 *   "Fits your <bike>"  ·  "Universal — fits any bike"  ·  "Doesn't fit your <bike>"
 * Re-parents the REAL theme product columns (.m-product-item) into per-group grids, so live price,
 * sale badges, dealer pricing, variants and Add-to-Cart all keep working (nothing is re-rendered).
 *
 * THE GRID IS NOT STATIC. Three separate theme behaviours change it underneath us, and each one used to
 * break grouping in its own way:
 *   1. numbered pagination — the grid only ever holds ONE page, so buckets and counts silently described
 *      a subset ("Fits 9" on a 13-product collection, the 13th sitting alone on page 2).
 *   2. load-more / infinite scroll — collection.js appends .m-product-item straight into the container,
 *      landing new cards OUTSIDE our group sections.
 *   3. storefront filters — storefont-filters.js does section.replaceWith(newSection) and emits NOTHING,
 *      so the grouping is thrown away with no notification.
 * We handle all three: merge every page up front (bounded), then observe the DOM and re-group whenever
 * the grid is appended to or swapped out. Counts are only shown when we know we have every product.
 *
 * Idempotent + re-runs on 'ymm:garage'. No deps.
 */
(function (w) {
  'use strict';
  function el(t, c, h) { var e = document.createElement(t); if (c) e.className = c; if (h != null) e.innerHTML = h; return e; }
  function esc(s) { return String(s == null ? '' : s).replace(/[&<>"]/g, function (m) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[m]; }); }
  // Full label incl. the make — "2024 Harley-Davidson Street Glide", not "2024 Street Glide".
  function shortLabel(v) { return v.label || [v.year, v.mk_name, v.model].filter(Boolean).join(' '); }

  // Product-type sub-groups inside a fitment group, each sorted most expensive first (Joe, 2026-07-16).
  // TYPE_ORDER is the merchandising order Joe named — bypass, usb piglet, media box, then parts. Screen
  // sits before Power Ports because it's a headline product, not an accessory. 'Accessories' is declared
  // ahead of use (Joe, 2026-07-16) so the day a product carries that type it slots in last instead of
  // sorting alphabetically among the unknowns. No product uses it yet; that's fine — this list is the
  // ORDER, not the contents, and a type with no products renders no heading.
  //
  // 'Other' is NOT in this list on purpose. It is the fallback for a product with NO product_type, and it
  // must stay conspicuous: if it ever renders, a product shipped untyped and wants fixing in admin.
  // Do not rename it to something plausible ("Universal", "Misc") — that hides the gap it exists to show,
  // and "Universal" is a FITMENT verdict that is already the outer heading. Two axes, two vocabularies.
  var TYPE_ORDER = ['Bypass', 'USB Piglet', 'Media Box', 'Screen', 'Power Ports', 'Accessories'];
  function byType(items) {
    var map = {}, seen = [];
    items.forEach(function (it) {
      var b = it.querySelector('.ymm-badge-slot');
      var t = (b && b.getAttribute('data-ymm-type')) || 'Other';
      if (!map[t]) { map[t] = []; seen.push(t); }
      map[t].push(it);
    });
    seen.sort(function (a, b) {
      var ia = TYPE_ORDER.indexOf(a), ib = TYPE_ORDER.indexOf(b);
      // unknown types (-1) go last, alphabetically among themselves — never interleaved with the known set
      if (ia < 0 && ib < 0) return a.localeCompare(b);
      if (ia < 0) return 1;
      if (ib < 0) return -1;
      return ia - ib;
    });
    return seen.map(function (t) {
      // price is cents on the slot; missing/unparseable sorts last rather than to the top as 0/NaN
      map[t].sort(function (x, y) { return priceOf(y) - priceOf(x); });
      return { type: t, items: map[t] };
    });
  }
  function priceOf(it) {
    var b = it.querySelector('.ymm-badge-slot');
    var p = b && Number(b.getAttribute('data-ymm-price'));
    return (p || p === 0) && isFinite(p) ? p : -1;
  }

  var ORDER = ['fits', 'universal', 'unknown', 'nofit'];
  // Bound the merge. A collection deeper than this would mean too many requests to fire on page load, so
  // we leave the theme's own pagination working and simply stop claiming counts we can't back up.
  var MAX_MERGE_PAGES = 8;

  var grid = null;          // the .m-collection-products container (=== [data-product-container])
  var order = null;         // items in their true DOM order, captured while ungrouped
  var mergeState = 'idle';  // idle → loading → done | skipped
  var complete = true;      // do we have EVERY product of this collection in the DOM?
  var observer = null;      // see quiet(): we must not observe our OWN writes
  var mergeRuns = 0;        // circuit breaker: a merge feeding the observer that triggers a merge…

  // Run a DOM write without the observer seeing it. A boolean flag CANNOT do this job: observer
  // callbacks fire asynchronously, so records queued during a synchronous write are delivered after the
  // flag is cleared — the observer then treats our own grouping as a theme change and re-groups forever
  // (verified: it locked the page up). Disconnecting drops the queued records outright.
  function quiet(fn) {
    if (observer) observer.disconnect();
    try { fn(); } finally {
      if (observer) { observer.takeRecords(); observer.observe(document.body, { childList: true, subtree: true }); }
    }
  }

  function gridEl() {
    if (grid && grid.isConnected) return grid;
    grid = document.querySelector('.m-collection-products');
    return grid;
  }
  function itemsIn(root) { return root ? [].slice.call(root.querySelectorAll('.m-product-item')) : []; }
  function totalPages() { var g = gridEl(); return g ? (Number(g.getAttribute('data-total-pages')) || 1) : 1; }

  function reset() { grid = null; order = null; mergeState = 'idle'; complete = true; }

  function pagePath(n) { var u = new URL(w.location.href); u.searchParams.set('page', n); return u.pathname + u.search; }

  function fetchPageItems(n) {
    return fetch(pagePath(n), { credentials: 'same-origin' })
      .then(function (r) { if (!r.ok) throw new Error('page ' + n + ' → ' + r.status); return r.text(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        return itemsIn(doc.querySelector('[data-product-container]'));
      });
  }

  // Pull every page into the grid so buckets and counts describe the whole collection. Rebuilds the list
  // from page 1..N rather than appending to what's here, so it's correct even when the visitor landed
  // directly on ?page=3. Appending server-rendered .m-product-item nodes is exactly what the theme's own
  // load-more does, so nothing about the cards is special-cased.
  function mergeAllPages() {
    if (mergeState !== 'idle') return Promise.resolve();
    var pages = totalPages();
    if (pages <= 1) { mergeState = 'done'; complete = true; return Promise.resolve(); }
    if (++mergeRuns > 4) {   // something is re-entering us; stop fetching rather than hammer the store
      mergeState = 'skipped'; complete = false;
      if (w.console) console.warn('[YMM] merge re-entered too many times — grouping this page only');
      return Promise.resolve();
    }
    if (pages > MAX_MERGE_PAGES) {
      mergeState = 'skipped'; complete = false;
      if (w.console) console.warn('[YMM] ' + pages + ' pages > cap ' + MAX_MERGE_PAGES + ' — grouping this page only, counts suppressed');
      return Promise.resolve();
    }
    mergeState = 'loading';
    var seq = []; for (var i = 1; i <= pages; i++) seq.push(i);
    return Promise.all(seq.map(fetchPageItems)).then(function (perPage) {
      var g = gridEl(); if (!g) return;
      quiet(function () {
        itemsIn(g).forEach(function (it) { it.remove(); });
        perPage.forEach(function (list) {
          list.forEach(function (it) { g.appendChild(document.importNode(it, true)); });
        });
        // every product is now on the page — the pager would be a lie, and leaving it live invites the
        // theme's load-more/infinite handler to append duplicates on top of what we just merged.
        var pag = document.querySelector('.m-collection--pagination');
        if (pag) pag.setAttribute('data-ymm-merged', '');
        // let the theme re-apply wishlist/compare state to the cards we brought in (its own hook).
        // Inside quiet(): its subscribers touch the DOM and we'd only be watching ourselves.
        try { w.MinimogEvents && w.MinimogEvents.emit('ON_PRODUCT_LIST_UPDATED'); } catch (e) {}
      });
      mergeState = 'done'; complete = true;
    }).catch(function (e) {
      if (w.console) console.warn('[YMM] page merge failed — grouping this page only:', e.message || e);
      mergeState = 'skipped'; complete = false;
    });
  }

  function restore() {
    var g = gridEl(); if (!g || !order) return;
    quiet(function () {
      g.classList.remove('ymm-grouped');
      // Give the sort control back — with no bike there's no grouping, so the theme's sort is honest again.
      document.documentElement.classList.remove('ymm-has-groups');
      order.forEach(function (it) { it.hidden = false; g.appendChild(it); });   // back to true order
      [].slice.call(g.querySelectorAll('.ymm-group')).forEach(function (s) { s.remove(); });
    });
  }

  function group() {
    if (!w.YMM || !w.YMM.engine) return;
    var g = gridEl(); if (!g) return;
    if (!order) order = itemsIn(g);
    restore();

    // The page's own bike wins over the garage. On /collections/fits-2024-…-street-glide the H1 and body
    // copy are about a Street Glide; grouping by the visitor's garage bike instead made the page call its
    // own products "Doesn't fit" (8 of 13 in the reported case).
    var pageVeh = w.YMM.pageVehicle && w.YMM.pageVehicle();
    var garage = w.YMMGarage && w.YMMGarage.get();
    var veh = pageVeh || garage;
    if (!veh) {
      // No vehicle set → still separate the grid by PRODUCT TYPE (category), so a mixed collection like
      // Apple CarPlay / Android Auto isn't one lump. Category-only, no fitment cut; the fit badges stay on
      // the cards. When a vehicle IS set, the fitment grouping below takes over. (Joe 2026-07-28 — keep the
      // leaf/radio category style everywhere.)
      quiet(function () {
        g.classList.add('ymm-grouped');
        byType(order).forEach(function (t) {
          var sec = el('section', 'ymm-group ymm-group--cat');
          sec.appendChild(el('h2', 'ymm-group__title ymm-group__title--cat', esc(t.type)));
          var sub = el('div', 'ymm-group__grid m:flex m:flex-wrap');
          t.items.forEach(function (i) { i.hidden = false; sub.appendChild(i); });
          sec.appendChild(sub);
          g.appendChild(sec);
        });
      });
      return;
    }
    // "your Street Glide" when it's the visitor's own bike (no page bike, or the page is about the same
    // bike they've saved); "a Street Glide" only when the page's bike isn't theirs.
    var mine = !pageVeh || (garage && w.YMM.collectionHandle(garage) === w.YMM.collectionHandle(pageVeh));
    var poss = mine ? 'your ' : 'a ';

    var titles = {
      fits: '✓ Fits ' + poss + shortLabel(veh),
      universal: '✓ Universal — fits ' + poss + shortLabel(veh),
      unknown: 'Fitment not listed',
      nofit: '✗ Doesn\'t fit ' + poss + shortLabel(veh),
    };
    var buckets = { fits: [], universal: [], unknown: [], nofit: [] };
    order.forEach(function (it) {
      var b = it.querySelector('[data-ymm-fit][data-sku]');
      var sku = b && b.getAttribute('data-sku');
      var v = sku ? w.YMM.verdict(sku, veh) : 'unknown';   // no SKU → unknown, never assume a fit
      (buckets[v] || buckets.unknown).push(it);
    });

    quiet(function () {
      g.classList.add('ymm-grouped');
      // Hide the theme's sort control while grouped (Joe, 2026-07-16). It still read "Alphabetically, A-Z"
      // while we render fitment → product type → price-desc, so it was announcing a sort that isn't in
      // effect. Flag the ROOT, not the grid: both controls (.m-toolbar--sortby desktop, .m-sortby--open
      // mobile) live in the toolbar, outside the grid, so no descendant selector can reach them from here.
      // Cleared in ungroup() — a visitor who clears their bike gets the control back.
      document.documentElement.classList.add("ymm-has-groups");
      // Nothing is ever hidden. Non-fitting parts render LAST, under their own heading, so the page still
      // answers "what else do you sell?" instead of pretending the rest of the catalogue doesn't exist.
      // (Previously ?ymm_fits=1 hid them outright — and fitUrl() sends every bike WITHOUT a landing page
      // to /collections/all?ymm_fits=1, so most drill-downs landed in that hidden mode. Joe, 2026-07-16.)
      ORDER.forEach(function (key) {
        var items = buckets[key];
        if (!items.length) return;
        var sec = el('section', 'ymm-group ymm-group--' + key);
        // Only show a number when we actually hold the whole collection. A count off by even one is worse
        // than no count — it's the page stating a fact about parts it never looked at.
        var count = complete ? ' <span class="ymm-group__count">' + items.length + '</span>' : '';
        sec.appendChild(el('h2', 'ymm-group__title', esc(titles[key]) + count));
        // Joe, 2026-07-16: "We need to keep the fitment seperated but we need to be able to sort it
        // inside of the fitment categories ... product type sub headers in the 'all' in conjuction with
        // the what fits" — so fitment stays the outer cut, product type becomes an inner one.
        byType(items).forEach(function (t) {
          sec.appendChild(el('h3', 'ymm-group__type', esc(t.type)));
          var sub = el('div', 'ymm-group__grid m:flex m:flex-wrap');
          t.items.forEach(function (i) { i.hidden = false; sub.appendChild(i); });
          sec.appendChild(sub);
        });
        g.appendChild(sec);
      });
    });
    try {
      w.document.dispatchEvent(new CustomEvent('ymm:grouped', { detail: {
        fits: buckets.fits.length, universal: buckets.universal.length,
        unknown: buckets.unknown.length, nofit: buckets.nofit.length, complete: complete,
      } }));
    } catch (e) {}
  }

  // merge (once) then group
  function run() {
    if (!w.YMM || !w.YMM.engine) return;
    if (!gridEl()) return;
    // only pay for the merge when we're actually going to group
    var haveVeh = (w.YMM.pageVehicle && w.YMM.pageVehicle()) || (w.YMMGarage && w.YMMGarage.get());
    if (!haveVeh) { group(); return; }
    mergeAllPages().then(function () {
      // Cards merged in from pages 2..N arrived after ymm-badges.js last ran, so their badge slots are
      // still empty and its shown/hidden counts (which drive the results banner) describe page 1 only —
      // the banner said "3 parts fit" while the groups said 5. Re-badge before grouping.
      quiet(function () { if (w.YMMBadges) w.YMMBadges.refresh(); });
      order = null;
      group();
    });
  }

  // The theme mutates this grid behind our back — load-more appends cards, filters replace the whole
  // section. Watch for both and re-group; without this, grouping is correct exactly until the visitor
  // touches a filter.
  function watch() {
    if (!w.MutationObserver || observer) return;
    observer = new MutationObserver(function (muts) {
      var hit = false;
      for (var i = 0; i < muts.length && !hit; i++) {
        var added = muts[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var n = added[j];
          if (n.nodeType !== 1) continue;
          if ((n.matches && (n.matches('.m-product-item') || n.matches('.m-collection-products'))) ||
              (n.querySelector && n.querySelector('.m-collection-products, .m-product-item'))) { hit = true; break; }
        }
      }
      if (!hit) return;
      reset();          // the grid may be a brand-new node (filters) — drop every cached reference
      run();
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  w.YMMGroups = { refresh: function () { reset(); run(); }, _state: function () { return { mergeState: mergeState, complete: complete, pages: totalPages(), items: order ? order.length : 0 }; } };
  document.addEventListener('ymm:garage', run);
  document.addEventListener('DOMContentLoaded', function () { if (w.YMM) w.YMM.onReady(function () { watch(); run(); }); });
})(window);
